import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, HttpError } from '../api/client';
import { Brand } from '../components/Brand';
import { landingPath } from '../components/Landing';
import { useAuth } from '../store/auth';

export default function Login() {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [operatorOpen, setOperatorOpen] = useState(false);
  const [operatorPw, setOperatorPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const setMe = useAuth((s) => s.setMe);

  /** Shared success tail: hydrate identity from /api/me, then land in the app. */
  async function finishLogin() {
    const me = await api.me();
    setMe(me);
    nav(landingPath(me.role), { replace: true });
  }

  function describeError(err: unknown, kind: 'email' | 'operator'): string {
    if (err instanceof HttpError && err.status === 401) {
      return kind === 'operator'
        ? "That password didn't match. Check for typos and try again."
        : "Email or password didn't match. Check for typos and try again.";
    }
    if (err instanceof HttpError && err.status === 429) {
      return 'Too many attempts. Wait a moment and try again.';
    }
    if (err instanceof HttpError) return `Sign-in failed: ${err.detail}`;
    return "Couldn't reach the server. Check your connection and try again.";
  }

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.authLogin(email.trim(), pw);
      await finishLogin();
    } catch (err: unknown) {
      setError(describeError(err, 'email'));
    } finally {
      setLoading(false);
    }
  }

  async function submitOperator(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await api.login(operatorPw);
      await finishLogin();
    } catch (err: unknown) {
      setError(describeError(err, 'operator'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* Hero panel */}
      <aside
        className="relative hidden overflow-hidden bg-gradient-to-br from-ff-mint-air via-ff-paper to-ff-blue-air p-14 lg:flex lg:flex-col lg:justify-between"
        aria-hidden="true"
      >
        <div className="pointer-events-none absolute -right-24 -top-32 h-[480px] w-[480px] rounded-full bg-ff-blue/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-ff-mint/55 blur-3xl" />

        <Brand className="relative z-10" linked={false} />

        <VisualStack />

        <div className="relative z-10 max-w-lg animate-rise">
          <p className="ff-kicker mb-6">The product creative workspace</p>
          <h1 className="ff-page-title text-[64px] leading-[0.94]">
            Branded product ads.
            <span className="block text-ff-orange">Across your catalog.</span>
          </h1>
          <p className="mt-7 max-w-md text-base leading-relaxed text-ff-ink-soft">
            Design once, connect the layout to product data, and keep every campaign
            creative consistent across the catalog.
          </p>
        </div>

        <footer className="relative z-10 flex items-end justify-between text-xs font-semibold text-ff-mute">
          <span>Design tied to product data</span>
          <span>Private workspace</span>
        </footer>
      </aside>

      {/* Sign-in panel */}
      <main className="relative flex items-center justify-center bg-ff-paper px-5 py-10 sm:px-12 sm:py-16">
        <div className="absolute inset-0 bg-grain opacity-25 pointer-events-none" />

        <div className="relative w-full max-w-md animate-rise rounded-2xl border border-ff-line bg-white p-6 shadow-soft sm:p-9">
          <div className="mb-10 lg:hidden">
            <Brand linked={false} />
          </div>

          <div>
            <p className="ff-kicker mb-3">Sign in</p>
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tightish text-ff-ink">
              Welcome back.
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-ff-mute">
              Sign in with the email and password your workspace admin gave you.
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="mt-6 flex items-start gap-3 rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-4 py-3 text-sm text-ff-danger"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submitEmail} className="mt-7 space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="ff-label">Email</label>
              <input
                id="email"
                data-testid="email"
                type="email"
                autoFocus
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="ff-input"
              />
            </div>
            <div>
              <label htmlFor="password" className="ff-label">Password</label>
              <input
                id="password"
                data-testid="password"
                type="password"
                autoComplete="current-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="••••••••"
                className="ff-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email || !pw}
              className="ff-btn-primary w-full py-3.5 text-base"
              data-testid="login-submit"
            >
              {loading ? (<><Spinner />Signing in</>) : (<>Sign in<span aria-hidden="true">→</span></>)}
            </button>
          </form>

          {/* Google — live OAuth is the operator's cloud last mile; stubbed for now. */}
          <button
            type="button"
            disabled
            data-testid="google-cta"
            title="Coming soon"
            className="ff-btn mt-4 w-full cursor-not-allowed border border-ff-line py-3 text-sm text-ff-mute/70"
          >
            <GoogleG />
            Continue with Google — coming soon
          </button>

          <p className="mt-6 border-t border-ff-line pt-5 text-xs leading-relaxed text-ff-mute">
            Access is invite-only. No account yet? Contact your admin for access.
          </p>

          {/* Operator (legacy shared-password) sign-in — disclosed, not primary. */}
          <div className="pt-5">
            <button
              type="button"
              data-testid="operator-toggle"
              onClick={() => setOperatorOpen((v) => !v)}
              className="text-xs font-semibold text-ff-mute transition-colors hover:text-ff-ink"
            >
              {operatorOpen ? '− Operator sign-in' : '+ Operator sign-in'}
            </button>
            {operatorOpen && (
              <form onSubmit={submitOperator} className="mt-4 space-y-3" noValidate>
                <label htmlFor="operator-pw" className="ff-label">Shared workspace password</label>
                <input
                  id="operator-pw"
                  data-testid="operator-password"
                  type="password"
                  autoComplete="off"
                  value={operatorPw}
                  onChange={(e) => setOperatorPw(e.target.value)}
                  placeholder="••••••••"
                  className="ff-input font-data tracking-widest"
                />
                <button
                  type="submit"
                  data-testid="operator-submit"
                  disabled={loading || !operatorPw}
                  className="ff-btn-ghost w-full py-2.5 text-sm"
                >
                  Sign in as operator
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function VisualStack() {
  return (
    <div className="pointer-events-none absolute right-8 top-24 h-64 w-64 opacity-85" aria-hidden="true">
      <div className="absolute left-1 top-5 h-40 w-40 -rotate-6 rounded-2xl border border-white bg-white/80 shadow-soft">
        <span className="absolute left-4 top-4 text-[9px] font-bold uppercase tracking-widish text-ff-mute">Catalog input</span>
        <span className="ff-checkerboard absolute inset-x-8 bottom-8 top-12 rounded-xl" />
      </div>
      <div className="absolute bottom-0 right-0 h-44 w-44 rotate-6 rounded-2xl border border-white/70 bg-ff-mint shadow-soft">
        <span className="absolute right-4 top-4 rounded-full bg-ff-orange px-2.5 py-1 text-[8px] font-bold text-white">CAMPAIGN</span>
        <span className="absolute bottom-5 left-5 rounded-xl bg-white px-3 py-2 text-sm font-extrabold text-ff-ink">Design once ↗</span>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#9aa0a6" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.4 17.7 9.5 24 9.5z" opacity="0.5" />
      <path fill="#9aa0a6" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.1 5.3-4.6 6.9l7.1 5.5c4.1-3.8 6.5-9.4 6.5-16.9z" opacity="0.5" />
      <path fill="#9aa0a6" d="M10.5 28.3c-.5-1.4-.8-2.9-.8-4.3s.3-3 .8-4.3l-7.9-6.1C1 16.7 0 20.2 0 24s1 7.3 2.6 10.4l7.9-6.1z" opacity="0.5" />
      <path fill="#9aa0a6" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.1-5.5c-2 1.3-4.5 2.1-8.8 2.1-6.3 0-11.6-3.9-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z" opacity="0.5" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
